import { rem } from 'polished'

import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import moment from 'moment'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { encodeSearchParams } from 'lib/search/searchUtils'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { getFormattedDateRange, getRecentSearchDescription } from 'components/Search/utils'
import Clickable from 'components/Common/Clickable'
import Typography from 'LuxKit/Typography/Typography'
import LineHistoryIcon from 'LuxKit/Icons/line/LineHistoryIcon'
import type { DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const SearchItem = styled(Clickable)`
  display: flex;
  gap: ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  padding: ${rem(12)} ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.S};
  align-items: center;
  transition: border-color 0.25s;

  ${mediaHoverable} {
    &:hover {
      border-color: ${props => props.theme.palette.neutral.default.one};
    }
  }
`

interface Props {
  onClick?: () => void
}

function getSubtitleTextFromRecentSearch(intl: IntlShape, recentSearch: App.FullRecentSearch) {
  const { dates, rooms } = recentSearch
  const dateText = getRecentSearchDescription(intl, dates.checkIn, dates.checkOut, dates.flexibleMonths, dates.flexibleDuration as DEFAULT_FLEXIBLE_DURATION_RANGE, dates.isAnytime)

  const occupancyText = formatOccupantsAsGuests(...rooms)

  const subtitleText = dateText !== '' && occupancyText !== '' && occupancyText !== '' ? intl.formatMessage(
    {
      id: 'UFTidb5sw_3G_rh5Qlb9',
      defaultMessage: '{dateText} · {occupancyText}',
      description: 'Recent search subtitle with dates and occupancy details',
    },
    {
      dateText,
      occupancyText,
    },
  ) : ''

  return subtitleText
}

function RecentSearches(props: Props) {
  const intl = useIntl()
  const { onClick } = props
  const dispatch = useAppDispatch()
  const recentSearches = useRecentSearches()

  function onClickHandler(recentSearch: App.FullRecentSearch) {
    onClick?.()
    const dates = {
      checkIn: recentSearch.dates?.checkIn,
      checkOut: recentSearch.dates?.checkOut,
    }
    const isFlexibleWithDate = !dates?.checkIn || !dates?.checkOut
    const queryParams = encodeSearchParams({
      urlSearch: '',
      searchItem: recentSearch.searchItem,
      dates,
      rooms: recentSearch.rooms,
      isFlexibleWithDate,
    }).toString()
    dispatch(pushWithRegion('/search', queryParams))
  }

  const anytimeLabel = intl.formatMessage({
    id: 'z7iBaB4zPgqvpKa4gnFx',
    defaultMessage: 'Anytime',
    description: 'Fallback date label shown in recent searches when no specific dates are selected',
  })

  const recentSearchItems = recentSearches.map((item) => {
    return {
      ...item,
      searchMainText: item.searchItem.format.mainText,
      occupancyText: formatOccupantsAsGuests(...item.rooms),
      datesText: item.dates?.checkIn && item.dates?.checkOut ?
        `${getFormattedDateRange(moment(item.dates.checkIn), moment(item.dates.checkOut))}` :
        anytimeLabel,
    }
  })

  if (!recentSearchItems.length) {
    return null
  }

  return (
    <LayoutContainer>
      <VerticalSpacer gap={12}>
        <Caption variant="large">{intl.formatMessage({
          id: 'qZH7y_ikRuYuqog2o5as',
          defaultMessage: 'Recent searches',
          description: 'Section heading for recent search history on the front page',
        })}
        </Caption>
        <CardCarousel gap={16}>
          {recentSearchItems.map((item) =>
            <SearchItem
              key={item.recentSearchId}
              onClick={() => onClickHandler(item)}
            >
              <LineHistoryIcon />
              <Typography wrap="truncate">
                <BodyText wrap="truncate" variant="small" weight="bold">
                  {item.searchItem.format.mainText}
                </BodyText>
                <Caption variant="large" colour="neutral-three">{getSubtitleTextFromRecentSearch(intl, item)}</Caption>
              </Typography>
            </SearchItem>,
          )}
        </CardCarousel>
      </VerticalSpacer>
    </LayoutContainer>
  )
}

export default RecentSearches
